import { useState } from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";
import {
  Building2,
  ChevronDown,
  ClipboardList,
  KeyRound,
  Layers,
  LayoutDashboard,
  ShieldCheck,
  UserPlus,
  Users,
  Wrench,
} from "lucide-react";
import { resolvePlatformShellGuard } from "@/pages/platform/platformShellGuard";

/** Flujo diario SaaS: solicitud → pago → emitir → control. */
export const PLATFORM_NAV_PRIMARY = [
  { to: "/platform/license-requests", label: "Solicitudes de licencia", icon: ClipboardList },
  { to: "/platform/licenses", label: "Licencias emitidas", icon: KeyRound },
  { to: "/platform/modules", label: "Planes comerciales", icon: Layers },
];

/** Soporte / rutas legacy; no se eliminan, solo se agrupan. */
export const PLATFORM_NAV_ADVANCED = [
  { to: "/platform/dashboard", label: "Panel", icon: LayoutDashboard },
  { to: "/platform/onboarding", label: "Alta comercial", icon: UserPlus },
  { to: "/platform/organizations", label: "Organizaciones", icon: Building2 },
  { to: "/platform/users", label: "Usuarios", icon: Users },
  { to: "/platform/commercial-tools", label: "Herramientas comerciales", icon: Wrench },
  { to: "/platform/safe-cleanup", label: "Limpieza segura", icon: ShieldCheck },
];

function NavItem({ to, label, icon: Icon, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
          isActive
            ? "bg-[#0F2D5C] text-white shadow-sm"
            : "text-[#0F2D5C] bg-white border border-[#0F2D5C]/15 hover:bg-[#0F2D5C]/5"
        }`
      }
    >
      <Icon className="w-4 h-4 shrink-0" aria-hidden />
      {label}
    </NavLink>
  );
}

export default function PlatformShell() {
  const { user, loading: authLoading } = useAuth();
  const { loading: capsLoading, data: capabilities } = useCapabilities();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const guard = resolvePlatformShellGuard({
    authLoading,
    capsLoading,
    user: user || null,
    capabilities,
  });

  if (guard.status === "loading") {
    return (
      <div
        className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-slate-50"
        data-testid="platform-shell-loading"
      >
        <p className="text-sm text-slate-600">Cargando Centro de Licencias…</p>
      </div>
    );
  }

  if (guard.status === "redirect") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50" data-testid="platform-shell">
      <div className="border-b border-[#0F2D5C]/15 bg-[#0F2D5C] text-white px-4 py-4 md:px-6">
        <h1 className="text-lg font-semibold tracking-tight">Centro de Licencias BNT</h1>
        <p className="text-xs text-white/75 mt-1 max-w-2xl">
          Revisa solicitudes, valida pago, emite el plan y consulta licencias activas.
        </p>
      </div>
      <div className="flex flex-col md:flex-row gap-0 md:gap-6 p-4 md:p-6 max-w-6xl mx-auto">
        <nav
          className="md:w-56 shrink-0 flex md:flex-col gap-1 overflow-x-auto pb-2 md:pb-0"
          aria-label="Centro de Licencias BNT"
          data-testid="platform-shell-nav"
        >
          {PLATFORM_NAV_PRIMARY.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}

          <div className="hidden md:block border-t border-slate-200 my-2" aria-hidden />

          <button
            type="button"
            className="inline-flex items-center justify-between gap-2 rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-100/80 border border-slate-200 hover:bg-slate-100 whitespace-nowrap"
            onClick={() => setAdvancedOpen((v) => !v)}
            aria-expanded={advancedOpen}
            data-testid="platform-nav-advanced-toggle"
          >
            Más opciones
            <ChevronDown
              className={`w-3.5 h-3.5 shrink-0 transition-transform ${advancedOpen ? "rotate-180" : ""}`}
              aria-hidden
            />
          </button>

          {advancedOpen ? (
            <div className="flex md:flex-col gap-1" data-testid="platform-nav-advanced">
              {PLATFORM_NAV_ADVANCED.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </div>
          ) : null}
        </nav>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
